import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HttpClientModule } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ScriptLoaderService } from "@ynet-core/services/script-loader.service";
import { FeatureRoutingModule } from './features/feature-routing.module';
import { FeatureComponent } from './features/feature.component';
import { SharedModule } from './shared/shared.module';
import { StoreModule } from '@ngrx/store';
import { reducers, metaReducers } from './app.reducer';
import { Select2Module } from '@ynet-core/select2/ng2-select2';
import { NgxDatatableModule } from '@swimlane/ngx-datatable'; // for ngx-datatable
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '@ynet-environments/environment';
import { UiEffects } from './shared/store/ui.effects';
import { EventService } from '@ynet-core/services/event.service';
import { UserService } from '@ynet-core/services/user.service';
import { ToastrModule } from 'ngx-toastr';
import { NgbModule, NgbDateParserFormatter, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { CustomNgbDateParserFormatter } from './features/settings/ngb-date-parser.component';
import { UserClaimsGuard } from './core/guards/user-claims.guard';
import { GlobalErrorHandler } from './core/handlers/global-error.handler';
import * as Sentry from '@sentry/angular-ivy';
import { Router } from '@angular/router';
import { SettingsService } from './core/services/settings.service';
import { RegistrationService } from './core/services/registration.service.';
import {PrintingService} from "@ynet-core/services/printing.service";
import { AbstractSecurityStorage, AuthModule, OidcSecurityService } from 'angular-auth-oidc-client';
import { OidcLocalStorageService } from './core/services/oidc-local-storage.service';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import * as fromRoot from './app.reducer';
import * as UI from './shared/store/ui.actions';

export function initAuth(oidcSecurityService: OidcSecurityService, store: Store<fromRoot.State>) {
    return () => oidcSecurityService.checkAuth().pipe(
        tap(({ isAuthenticated }) => {
            if (isAuthenticated) {
                store.dispatch(new UI.GetUserInfo());
            }
        }),
        catchError(err => {
            // checkAuth threw — typically "could not find matching config for state".
            // Happens when a callback URL (?state=...&code=...) is opened in a fresh
            // tab whose sessionStorage doesn't have the matching state, or when
            // storage was cleared mid-flow. Strip the stale params and restart auth
            // so the next round-trip stores + validates state in this tab.
            // Guard against infinite recovery loops via a session flag.
            // eslint-disable-next-line no-console
            console.warn('Auth init failed, recovering:', err);
            const recoveryFlag = 'em3-auth-recovery';
            window.history.replaceState({}, document.title, window.location.pathname);
            if (!sessionStorage.getItem(recoveryFlag)) {
                sessionStorage.setItem(recoveryFlag, '1');
                oidcSecurityService.authorize(undefined, {
                    customParams: { prompt: 'select_account' }
                });
            }
            return of({ isAuthenticated: false } as any);
        }),
        tap(({ isAuthenticated }) => {
            if (isAuthenticated) {
                sessionStorage.removeItem('em3-auth-recovery');
            }
        })
    );
}



@NgModule({
    declarations: [
        FeatureComponent,
        AppComponent,
    ],
    imports: [
        Select2Module,
        NgxDatatableModule,
        BrowserModule,
        HttpClientModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        FeatureRoutingModule,
        NgbModule,
        NgbTypeaheadModule,
        SharedModule,
        StoreModule.forRoot(reducers, { metaReducers }),
        // StoreRouterConnectingModule.forRoot({
        //     stateKey: 'router', // name of reducer key
        // }),
        EffectsModule.forRoot([UiEffects]),  //TODO:  UI Effects?
        StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
            logOnly: environment.production // Restrict extension to log-only mode
        }),
        // AmChartsModule,
        ToastrModule.forRoot(),
        AuthModule.forRoot({
            config: {
                authority: environment.keycloakAuthority,
                redirectUrl: window.location.origin,
                postLogoutRedirectUri: window.location.origin,
                clientId: 'em3-admin',
                scope: 'openid profile email',
                responseType: 'code',
                useRefreshToken: true,
                renewTimeBeforeTokenExpiresInSeconds: 30,
                secureRoutes: [environment.api.baseUrl],
            }
        }),
    ],
    exports: [
        // SharedModule
    ],
    providers: [
        ScriptLoaderService,
        UserClaimsGuard,
        EventService,
        UserService,
        RegistrationService,
        PrintingService,
        SettingsService,
        { provide: NgbDateParserFormatter, useFactory: () => new CustomNgbDateParserFormatter() },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        // Sentry route tracing -- Sentry.TraceService instruments Angular Router
        // so navigations show up as transactions; the no-op factory APP_INITIALIZER
        // just ensures the service is constructed at bootstrap time.
        { provide: Sentry.TraceService, deps: [Router] },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initAuth,
            deps: [OidcSecurityService, Store],
            multi: true
        },
        // Move OIDC token storage from sessionStorage (per-tab) to localStorage
        // (shared across same-origin tabs). Lets a user open em3.admin in a
        // new tab without being forced through Keycloak login again.
        { provide: AbstractSecurityStorage, useClass: OidcLocalStorageService },
        // NOTE: a 401-retry interceptor was added in PR #675 here. Removed
        // after the cancel-attendee 428 flow was observed propagating to
        // GlobalErrorHandler instead of being caught by ConfirmationInterceptor
        // -- the duplicate interceptor (the existing AuthHttpIntercepter in
        // SharedModule already handles 401 retry via sendWithRetry) was the
        // only response-chain addition that could break the 428 flow.
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
